import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PricePoint {
  date: string;
  close: number;
  sma2?: number;
  sma12?: number;
  sma50?: number;
  sma200?: number;
}

interface PriceHistoryChartProps {
  ticker: string;
  currency?: string;
}

const calculateSMA = (data: PricePoint[], period: number, key: 'sma2' | 'sma12' | 'sma50' | 'sma200') => {
  return data.map((point, idx, arr) => {
    if (idx < period - 1) return { ...point, [key]: null };
    
    const slice = arr.slice(Math.max(0, idx - period + 1), idx + 1);
    const sum = slice.reduce((acc, p) => acc + p.close, 0);
    return { ...point, [key]: sum / slice.length };
  });
};

export function PriceHistoryChart({ ticker, currency }: PriceHistoryChartProps) {
  const [data, setData] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPriceHistory();
  }, [ticker]);

  const fetchPriceHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: historyData, error: fetchError } = await supabase.functions.invoke('fetch-price-history', {
        body: { ticker, currency }
      });

      if (fetchError) throw fetchError;

      if (!historyData?.prices || historyData.prices.length === 0) {
        throw new Error('No price history available');
      }

      // Calculate SMAs
      let processedData = historyData.prices;
      processedData = calculateSMA(processedData, 2, 'sma2');
      processedData = calculateSMA(processedData, 12, 'sma12');
      processedData = calculateSMA(processedData, 50, 'sma50');
      processedData = calculateSMA(processedData, 200, 'sma200');

      setData(processedData);
    } catch (err) {
      console.error('Error fetching price history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load price history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            6-Month Price History
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            6-Month Price History
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          6-Month Price History & SMAs
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={['auto', 'auto']}
              tickFormatter={(value) => value.toFixed(2)}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--background))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
              formatter={(value: number) => value.toFixed(2)}
              labelFormatter={(label) => new Date(label).toLocaleDateString()}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="hsl(var(--primary))" 
              strokeWidth={2}
              dot={false}
              name="Price"
            />
            <Line 
              type="monotone" 
              dataKey="sma2" 
              stroke="#10b981" 
              strokeWidth={1.5}
              dot={false}
              name="SMA 2"
              strokeDasharray="5 5"
            />
            <Line 
              type="monotone" 
              dataKey="sma12" 
              stroke="#3b82f6" 
              strokeWidth={1.5}
              dot={false}
              name="SMA 12"
            />
            <Line 
              type="monotone" 
              dataKey="sma50" 
              stroke="#f59e0b" 
              strokeWidth={1.5}
              dot={false}
              name="SMA 50"
            />
            <Line 
              type="monotone" 
              dataKey="sma200" 
              stroke="#ef4444" 
              strokeWidth={1.5}
              dot={false}
              name="SMA 200"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
